import { SportIcon } from '@bz/systemAssets';
import { createLazyMemo } from '@solid-primitives/memo';
import { useNavigate } from '@solidjs/router';

import { BetSlipType, SingleSubType } from '../../types';
import { BetInput } from './betItemCom/BetInput';
import { BetStakeInputTip } from './betItemCom/BetStakeInputTip';
import { useBetSlipStore } from '../../useBetSlip';
import '../css.css';
import { useMatchDetail } from '../match.context';
import { useBonusConfig } from '../shared/hooks';
import { Tooltip } from './Tooltip';

import { createLatestOdds } from '~/primitives/createOddsWs';
import { MarketStatus, OutcomeStatus } from '~/constants';

export const BetItem = (
  props: {
    odd: any;
    index: number;
    subIndex: number;
  } & { onRemove: () => void }
) => {
  const [store, dispatch] = useBetSlipStore();
  const navigate = useNavigate();
  const match = useMatchDetail(() => props.odd.matchId);

  createEffect(() => {
    console.log('bet item odds changed', match(), latestOdds);
  });
  const { minBetOdds } = useBonusConfig();
  const [isStakeTipVisible, setIsStakeTipVisible] = createSignal(false);

  // Fetch odds updates in real time via WebSocket and compute the odds delta.
  const latestOdds = createLatestOdds(() => props.odd);
  const isShowOddsLimit = createMemo(() => {
    if (latestOdds.type === SingleSubType.CustomBet) {
      return latestOdds.selections?.some((s: any) => s.odds < minBetOdds());
    }
    return latestOdds.odds < minBetOdds();
  });
  let el!: HTMLDivElement;

  onMount(() => {
    requestAnimationFrame(() => {
      el.classList.add('bet-item-enter-active');
    });
  });

  // Track odds movement.
  const diff = createMemo(
    on(
      () => latestOdds.odds,
      (newVal, oldVal) => {
        if (oldVal === undefined || newVal === oldVal) return 0;
        return newVal - oldVal;
      }
    )
  );

  // Market status // -1, -2, 0 -> Suspended; -3: settled; -4: cancelled
  const oddsStatusMask = createLazyMemo(() => {
    const { marketStatus, active } = latestOdds as any;

    // The market has been settled or cancelled.
    if (
      marketStatus === MarketStatus.Settled ||
      marketStatus === MarketStatus.Cancelled ||
      marketStatus === MarketStatus.Deactivated
    ) {
      return { status: 'closed', label: t('Closed') };
    }

    // The market is suspended.
    if (marketStatus === MarketStatus.Suspended) {
      return { status: 'suspended', label: t('Suspended') };
    }

    // The market is open; the final status depends on the individual outcome status.
    return active === OutcomeStatus.Active
      ? { status: 'active', label: null }
      : { status: 'suspended', label: t('Suspended') };
  });

  createComputed(
    on(
      () => latestOdds.diff,
      (diff) => {
        diff !== 0 &&
          dispatch({
            type: 'MARK_BET_CHANGED',
            code: props.odd.code,
          });
      }
    )
  );

  const IsCombo = createMemo(() => store.betType === BetSlipType.Combo);
  const isSingle = createMemo(() => store.betType === BetSlipType.Single);
  const isSystem = createMemo(() => store.betType === BetSlipType.System);
  const navigateToMatchDetail = (matchId?: string) => {
    if (!matchId) return;
    navigate(`/match/${matchId}`);
  };

  const navigateToOutrightDetail = (e: MouseEvent, outright: any) => {
    e.stopPropagation();
    if (!outright?.matchId) return;

    const sport = outright?.sportName || match()?.sportName || '';
    const categoryName = outright?.categoryName || match()?.categoryName || '';
    const seasonName = outright?.seasonName || outright?.name || match()?.seasonName || '';
    const sportId = outright?.sportId || match()?.sportId || '';
    const categoryId = outright?.categoryId || match()?.categoryId || '';

    const search = new URLSearchParams({
      sport,
      categoryName,
      seasonName,
      sportId,
      categoryId,
    }).toString();

    navigate(`/outright/${outright.matchId}?${search}`, {
      state: {
        from: 'sport',
        sport,
        categoryName,
        seasonName,
        sportId,
        categoryId,
      },
    });
  };

  // custom content
  const CustomBetContent = (props: { odds: any; onRemove: () => void }) => {
    const selections = createMemo(() => props.odds.selections);

    return (
      <div class="relative rounded-lg">
        <StatusMask />
        <div class="flex">
          <RemoveItem isCustomBet />
          <div class={cx('w-[calc(100%-1.5rem)] bg-layer5_5-card', IsCombo() ? '' : 'rounded-tr-lg rounded-br-lg')}>
            <ContentHeader type={SingleSubType.CustomBet} />
            {/* Content */}
            <div class="px-2 pb-2">
              <For each={selections()}>
                {(m, index) => (
                  <div class="flex bg-layer3_5-card relative rounded border-main3 mb-[3px]">
                    <div class="flex flex-col items-center flex-shrink-0 w-6 ml-1 relative">
                      <Show when={index() > 0}>
                        <div class="w-0 border-l-2 border-dashed border-[var(--text-brand-default\_alt)] opacity-30 h-3" />
                      </Show>
                      <div
                        class={cx(
                          'w-2 h-2 rounded-full border-2 border-[var(--text-brand-default\\_alt)] flex-shrink-0',
                          index() === 0 ? 'mt-3' : ''
                        )}
                      />
                      <Show when={index() < selections().length - 1}>
                        <div class="w-0 border-l-2 border-dashed border-[var(--text-brand-default\_alt)] opacity-30 flex-grow" />
                      </Show>
                    </div>
                    {/* Right-side content */}
                    <div class="flex-1 min-w-0 flex flex-col justify-center py-2 pr-1">
                      <div class="flex justify-between items-center">
                        <div class="flex-1 min-w-0">
                          <div class="text-sm text-neutral-main1 font-semibold leading-tight">
                            <Show when={m.colName}>
                              <span>{m.colName} / </span>
                            </Show>
                            <span>{m?.outcomeName}</span>
                          </div>
                          <span class="text-neutral-main2 caption-s-medium mb-1">{m.marketName}</span>
                        </div>
                      </div>
                      <Show when={index() < selections().length - 1}>
                        <div class="absolute -bottom-[3px] left-0 right-0 h-[3px] bg-[linear-gradient(to_right,#18232B_3px,transparent_3px)] bg-[length:6px_3px]"></div>
                      </Show>
                    </div>
                  </div>
                )}
              </For>

              <div class="flex font-semibold justify-between my-2">
                <span class="text-neutral-main2">{t('Total Odds')}</span>
                <span class="center text-neutral-main1 font-semibold">
                  <OddsFormat value={props.odds.odds} />
                </span>
              </div>
              <BetInput
                disabled={oddsStatusMask().status !== 'active'}
                amount={props.odds.amount}
                matchId={props.odds.matchId}
                code={props.odds.code}
                betType={BetSlipType.Single}
                hasError={isStakeTipVisible()}
              />
            </div>
          </div>
        </div>
        {/* Stake limit tip */}
        <Show when={props.odds.amount !== 0 && oddsStatusMask().status !== 'closed'}>
          <BetStakeInputTip
            amount={props.odds.amount}
            code={props.odds.code}
            matchId={props.odds.matchId}
            isShowOddsLimit={isShowOddsLimit}
            onVisibleChange={setIsStakeTipVisible}
          />
        </Show>
      </div>
    );
  };

  // Market status overlay.
  const StatusMask = () => {
    return (
      <Show when={oddsStatusMask().status === 'closed'}>
        <div class="absolute z-[11] top-0 left-6 right-0 center h-full bg-alpha-bw-autoblack-a50">
          <div class="text-neutral-main2 font-semibold text-2xl w-full h-full center bg-layer5_5-card rounded">
            <div class="center text-neutral-main3">
              <IconLock class="size-5 mr-1" />
              <span class="caption-l-semibold">{oddsStatusMask().label}</span>
            </div>
          </div>
        </div>
      </Show>
    );
  };

  // left close button
  const RemoveItem = ({ isCustomBet }: { isCustomBet?: boolean }) => {
    return (
      <div class={cx('w-6 center bg-layer6', !!isCustomBet ? 'rounded-tl-lg rounded-bl-lg' : '')}>
        <button class="size-6 center text-neutral-main3 hover:text-brand-default" onClick={() => props.onRemove()}>
          <IconCloseMark class="size-4" />
        </button>
      </div>
    );
  };

  // content
  const ItemContent = () => {
    const OddsDisplay = () => {
      return (
        <span
          class="inline-flex items-center gap-1 caption-l-semibold text-function-open"
          classList={{
            '!text-function-success': diff() > 0,
            '!text-function-error': diff() < 0,
          }}
        >
          <IconArrow04
            class="size-6 -mr-2"
            classList={{
              'rotate-[-180deg]': diff() > 0,
              hidden: diff() === 0,
            }}
          />

          <OddsFormat value={latestOdds.odds} />
        </span>
      );
    };

    return (
      <div class="flex text-neutral-main1 justify-between mb-[0.38rem]">
        <div class="inline-flex flex-col justify-start text-left items-start min-w-0 flex-1">
          <span class="flex justify-between w-full">
            <span class="caption-m-semibold max-w-[250px] block truncate whitespace-nowrap">
              <Show when={latestOdds.colName}>
                <span>{latestOdds.colName} / </span>
              </Show>
              {latestOdds?.outcomeName}
            </span>
            <Switch>
              <Match when={oddsStatusMask().status === 'suspended'}>
                <span class="center text-function-alert text-[.875rem]">
                  <IconLock class="size-4"></IconLock>
                  <span>{t('Suspended')}</span>
                </span>
              </Match>
              <Match when={oddsStatusMask().status === 'active'}>
                <OddsDisplay />
              </Match>
            </Switch>
          </span>
          <span class="caption-m-medium text-neutral-main2 block max-w-[18.75rem] truncate">
            {latestOdds.marketName}
          </span>
        </div>
      </div>
    );
  };

  // content header
  const ContentHeader = (props: { type: SingleSubType }) => {
    return (
      <div class="flex text-sm text-neutral-oncolor1 items-center px-2 pt-1">
        <div class="flex items-center flex-1 min-w-0 py-1">
          <Show when={match()}>
            {(m) => {
              return (
                <span class="flex items-center min-w-0 flex-1 text-neutral-main2 caption-m-medium">
                  <Show when={m().sportName}>
                    <SportIcon class="size-4 flex-shrink-0 text-neutral-main1_alt" sportId={m().sportId}></SportIcon>
                  </Show>
                  <Show
                    when={props.type !== SingleSubType.Outright}
                    fallback={
                      <span
                        class="block truncate cursor-pointer max-w-[16rem]"
                        role="link"
                        tabindex="0"
                        onClick={(e) => navigateToOutrightDetail(e, latestOdds)}
                      >
                        {latestOdds?.seasonName || m().seasonName}
                      </span>
                    }
                  >
                    <Tooltip content={`${m().homeName} vs ${m().awayName}`}>
                      <span
                        class="block truncate cursor-pointer max-w-[16rem]"
                        role="link"
                        tabindex="0"
                        onClick={() => navigateToMatchDetail(m().id)}
                      >
                        {m().homeName} vs {m().awayName}
                      </span>
                    </Tooltip>
                  </Show>
                </span>
              );
            }}
          </Show>
        </div>
        <Show when={(latestOdds as any)?.phase}>
          <IconLive class="size-4 text-function-live flex-shrink-0 ml-1" />
        </Show>
      </div>
    );
  };

  return (
    <div ref={el} class="bet-item bet-item-enter">
      <Switch fallback={<div>{t('no data')}</div>}>
        <Match when={props.odd.type === SingleSubType.CustomBet}>
          <CustomBetContent odds={props.odd} onRemove={props.onRemove} />
        </Match>
        <Match when={props.odd.type === SingleSubType.Outright}>
          <div class={cx('relative overflow-hidden rounded-lg')}>
            <StatusMask />
            <div class="flex">
              {/* left close */}
              <RemoveItem />
              {/* right content */}
              <div class={cx('w-[calc(100%-1.5rem)] bg-layer5_5-card')}>
                <ContentHeader type={SingleSubType.Outright} />
                {/* Content */}
                <div class="px-2 pb-2">
                  <ItemContent />
                  {/* Bet Amount Input */}
                  <Toggle when={store.betType === BetSlipType.Single}>
                    <BetInput
                      disabled={oddsStatusMask().status !== 'active'}
                      amount={props.odd.amount}
                      matchId={props.odd.matchId}
                      code={props.odd.code}
                      betType={BetSlipType.Single}
                      hasError={isStakeTipVisible()}
                    />
                  </Toggle>
                </div>
              </div>
            </div>

            {/* Stake limit tip */}
            <Show
              when={
                props.odd.amount !== 0 && store.betType === BetSlipType.Single && oddsStatusMask().status !== 'closed'
              }
            >
              <BetStakeInputTip
                amount={props.odd.amount}
                code={props.odd.code}
                isShowOddsLimit={isShowOddsLimit}
                onVisibleChange={setIsStakeTipVisible}
              />
            </Show>
          </div>
        </Match>
        <Match when={props.odd.type === SingleSubType.Single}>
          <div
            class={cx(
              'relative overflow-hidden',
              isSingle() || isSystem()
                ? 'rounded-lg'
                : IsCombo() && props.index === 0 && props.subIndex === 0
                  ? 'rounded-t-lg'
                  : ''
            )}
          >
            <StatusMask />
            <div class="flex">
              {/* left close */}
              <RemoveItem />
              {/* right content */}
              <div class={cx('w-[calc(100%-1.5rem)] bg-layer5_5-card')}>
                <ContentHeader type={SingleSubType.Single} />
                {/* Content */}
                <div class="px-2 pb-2">
                  <ItemContent />
                  {/* Bet Amount Input */}
                  <Toggle when={store.betType === BetSlipType.Single}>
                    <BetInput
                      disabled={oddsStatusMask().status !== 'active'}
                      amount={props.odd.amount}
                      matchId={props.odd.matchId}
                      code={props.odd.code}
                      betType={BetSlipType.Single}
                      hasError={isStakeTipVisible()}
                    />
                  </Toggle>
                </div>
              </div>
            </div>
            {/* Stake limit tip */}
            <Show
              when={
                props.odd.amount !== 0 && store.betType === BetSlipType.Single && oddsStatusMask().status !== 'closed'
              }
            >
              <BetStakeInputTip
                amount={props.odd.amount}
                code={props.odd.code}
                isShowOddsLimit={isShowOddsLimit}
                onVisibleChange={setIsStakeTipVisible}
              />
            </Show>
          </div>
        </Match>
      </Switch>
    </div>
  );
};
